import React, { useEffect, useState } from "react";
import { Users, X, Plus } from "lucide-react";
import { api } from "./api";

/**
 * Reusable share picker. Lists HEXA team members from /api/users/directory
 * and lets the user select multiple @hexawebos.com addresses.
 *
 * Props:
 *   value:   string[]   currently selected addresses
 *   onChange: (string[]) => void
 *   label:   string     optional label (default "Share with")
 */
export default function SharePicker({ value = [], onChange, label = "Share with" }) {
  const [directory, setDirectory] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [manual, setManual] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/users/directory")
      .then(r => { if (!cancelled) setDirectory(r.data || []); })
      .catch(() => { if (!cancelled) setDirectory([]); });
    return () => { cancelled = true; };
  }, []);

  const toggle = (addr) => {
    const a = addr.toLowerCase();
    if (value.includes(a)) onChange(value.filter(v => v !== a));
    else onChange([...value, a]);
  };

  const addManual = () => {
    const a = manual.trim().toLowerCase();
    if (!a) return;
    if (!a.endsWith("@hexawebos.com")) return;
    if (!value.includes(a)) onChange([...value, a]);
    setManual("");
  };

  const filtered = directory.filter(u => {
    if (!filter.trim()) return true;
    const f = filter.toLowerCase();
    return (u.address || "").includes(f) || (u.name || "").toLowerCase().includes(f);
  });

  return (
    <div data-testid="share-picker">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1">
          <Users size={10} /> {label}
        </span>
        <span className="text-[9px] text-white/30">@hexawebos.com only</span>
      </div>

      {/* Selected chips */}
      <div className="min-h-[28px] flex flex-wrap gap-1 mb-2">
        {value.length === 0 && (
          <span className="text-[10px] text-white/30 italic">Not shared — only you can see this</span>
        )}
        {value.map(addr => (
          <span
            key={addr}
            data-testid={`share-chip-${addr}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] mono rounded-sm bg-[#8b5cf6]/15 text-[#a78bfa] border border-[#8b5cf6]/40"
          >
            {addr}
            <button
              type="button"
              onClick={() => toggle(addr)}
              className="hover:text-[#f87171]"
              aria-label={`remove ${addr}`}
            >
              <X size={9} />
            </button>
          </span>
        ))}
      </div>

      {/* Directory list */}
      <button
        type="button"
        data-testid="share-picker-toggle"
        onClick={() => setOpen(o => !o)}
        className="text-[10px] mono uppercase tracking-widest text-[#c7c1ff] hover:text-[#8b5cf6]"
      >
        {open ? "// hide team" : "// pick from team"}
      </button>

      {open && (
        <div className="mt-1 border border-white/10 bg-black/30 rounded-sm">
          <input
            data-testid="share-picker-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter by name or address"
            className="w-full bg-transparent border-b border-white/10 px-2 py-1 text-[11px] outline-none"
          />
          <div className="max-h-32 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-2 py-2 text-[10px] text-white/40 italic">No teammates found.</div>
            )}
            {filtered.map(u => {
              const selected = value.includes(u.address);
              return (
                <button
                  key={u.address}
                  type="button"
                  data-testid={`share-picker-row-${u.address}`}
                  onClick={() => toggle(u.address)}
                  className={`w-full text-left px-2 py-1.5 text-[11px] flex items-center justify-between hover:bg-white/5 ${selected ? "bg-[#8b5cf6]/10" : ""}`}
                >
                  <span className="truncate">
                    <span className="text-white/85">{u.name || u.username}</span>
                    <span className="text-white/45 ml-2 mono">{u.address}</span>
                  </span>
                  {selected && <span className="text-[#8b5cf6] text-[10px] mono">✓ added</span>}
                </button>
              );
            })}
          </div>
          {/* Manual add */}
          <div className="flex items-center gap-1 border-t border-white/10 p-1">
            <input
              data-testid="share-picker-manual"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addManual())}
              placeholder="user@hexawebos.com"
              className="flex-1 bg-transparent border border-white/10 rounded-sm px-2 py-1 text-[11px] outline-none focus:border-[#8b5cf6]/40"
            />
            <button
              type="button"
              data-testid="share-picker-manual-add"
              onClick={addManual}
              disabled={!manual.trim().toLowerCase().endsWith("@hexawebos.com")}
              className="p-1 text-[#c7c1ff] hover:text-[#8b5cf6] disabled:opacity-30"
              title="Add address"
            >
              <Plus size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
